import { request } from '../http';

export type Organization = {
  id: string;
  name: string;
  slug?: string | null;
  status?: string | null;
  timezone?: string;
  defaultLanguage?: string;
  logoUrl?: string | null;
  notificationDefaults?: Record<string, unknown>;
  searchDefaults?: Record<string, unknown>;
  isInternal?: boolean;
  workspaceType?: string | null;
};

export type SourceAccessSummary = {
  assignedCount: number;
  workspaceEnabledCount: number;
  sources: Array<{ id: string; name: string; displayName?: string | null }>;
};

export type OrgMember = {
  id: string;
  userId: string;
  name?: string | null;
  email?: string | null;
  membershipRole: string;
  membershipRoleLabel?: string | null;
  status?: string | null;
  joinedAt?: string | null;
  lastLoginAt?: string | null;
  invitedBy?: string | null;
  invitedByName?: string | null;
  platformRole?: string | null;
  accountEnabled?: boolean | null;
  plan?: string | null;
  workspacePlanSlug?: string | null;
  workspaceUnlimited?: boolean;
  effectivePlan?: import('@/utils/effectivePlanDisplay').EffectivePlan | null;
  sourceAccess?: SourceAccessSummary | null;
};

export type OrgMemberCandidate = {
  id: string;
  name?: string | null;
  email?: string | null;
  platformRole?: string | null;
  plan?: string | null;
  organizations?: string[];
};

export type OrgInvitation = {
  id: string;
  organizationId: string;
  email: string;
  membershipRole: string;
  status: string;
  expiresAt?: string | null;
  organizationName?: string | null;
};

export type OrganizationMemberSummary = {
  total: number;
  workspaceAdmins: number;
  editors: number;
  viewers: number;
  pendingInvitations: number;
};

export type OrgDashboard = {
  organization: Organization | null;
  members: OrgMember[];
  pendingInvitations: OrgInvitation[];
  memberSummary?: OrganizationMemberSummary | null;
  searchCount: number;
  recipientCount: number;
  usage: Record<string, unknown>;
  health: Record<string, unknown>;
};

export type MemberActivity = {
  id: string;
  action: string;
  actorName?: string | null;
  actorEmail?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, unknown>;
};

export const organizationsService = {
  current() {
    return request<Organization>('/organizations/current');
  },

  dashboard() {
    return request<OrgDashboard>('/organizations/current/dashboard');
  },

  members() {
    return request<{ items: OrgMember[] }>('/organizations/current/members');
  },

  memberCandidates(q?: string) {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<{ items: OrgMemberCandidate[] }>(`/organizations/current/members/candidates${query}`);
  },

  addMember(userId: string, membershipRole: string) {
    return request<OrgMember>('/organizations/current/members', {
      method: 'POST',
      body: JSON.stringify({ userId, membershipRole }),
    });
  },

  addMembersBulk(userIds: string[], workspaceRole: string) {
    return request<{
      added: number;
      skipped: string[];
      errors: Array<{ userId: string; reason: string }>;
    }>('/organizations/current/members/bulk', {
      method: 'POST',
      body: JSON.stringify({ userIds, workspaceRole }),
    });
  },

  invite(email: string, membershipRole: string) {
    return request<OrgInvitation>('/organizations/current/invite', {
      method: 'POST',
      body: JSON.stringify({ email, membershipRole }),
    });
  },

  updateMember(
    membershipId: string,
    body: {
      membershipRole?: string;
      membershipStatus?: string;
      accountEnabled?: boolean;
      sourceIds?: string[];
    },
  ) {
    return request<OrgMember>(`/organizations/current/members/${membershipId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  removeMember(membershipId: string) {
    return request<{ ok: boolean }>(`/organizations/current/members/${membershipId}`, {
      method: 'DELETE',
    });
  },

  invitations() {
    return request<{ items: OrgInvitation[] }>('/organizations/current/invitations');
  },

  revokeInvitation(invitationId: string) {
    return request<{ ok: boolean }>(`/organizations/current/invitations/${invitationId}/revoke`, {
      method: 'POST',
    });
  },

  activity() {
    return request<{ items: MemberActivity[] }>('/organizations/current/activity');
  },

  updateSettings(body: Partial<Organization>) {
    return request<Organization>('/organizations/current', {
      method: 'PATCH',
      body: JSON.stringify({
        name: body.name,
        timezone: body.timezone,
        defaultLanguage: body.defaultLanguage,
        logoUrl: body.logoUrl,
      }),
    });
  },

  acceptInvitation(token: string) {
    return request<{ ok: boolean; organizationId: string }>(`/invitations/${token}/accept`, {
      method: 'POST',
    });
  },

  getInvitation(token: string) {
    return request<OrgInvitation>(`/invitations/${token}`);
  },

  platformList(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<{ items: Organization[] }>(`/organizations/platform${q}`);
  },

  platformCreate(body: { name: string; slug?: string; workspaceType?: string }) {
    return request<Organization>('/organizations/platform', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  platformUpdate(organizationId: string, body: { name: string; timezone?: string; defaultLanguage?: string }) {
    return request<Organization>(`/organizations/platform/${organizationId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  platformDelete(organizationId: string, confirmationName: string) {
    return request<{ ok: boolean; organizationId: string; name: string }>(
      `/organizations/platform/${organizationId}/delete`,
      {
        method: 'POST',
        body: JSON.stringify({ confirmationName }),
      },
    );
  },
};
